/**
 * The Visit reschedule dialog: the new appointment in
 * the Visit's timezone or an upcoming Visit of the line to join, a live preview of where each task lands, the
 * booking choice for a booked Visit and the compliance-window confirmation. Submitting sends the reviewed preview's fingerprint; a stale review keeps
 * the dialog open on the refreshed preview with the person's choices untouched.
 */
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, resource, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityEntityName, hasVisitComplianceWindow, MetadataEntityFieldEditorType, MetadataEntityFieldType, SurfaceType, VisitBrowseRow, visitSpacingWarning, isFullyApproved } from '@jms/model';
import { VisitDateOverride, visitRescheduleAllowed, visitRescheduleFloor } from '@jms/model/entities/planned.work/visit/visit.dates';
import { EntityDataService } from '@workbuddy/services/entities';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { resolveBulkActionErrorMessage } from '@workbuddy/ui/v2/blocks/entity.board/model';
import { defineSurface, defineSurfaceRuntime, defineSurfaceSchema, WbSurfaceComponent } from '@workbuddy/ui/v2/surface.engine';
import { DateTime } from 'luxon';
import { catchError, debounceTime, from, lastValueFrom, map, Observable, of, Subject, switchMap } from 'rxjs';
import { summariseVisitMovePreview, VisitMovePreviewSummary } from '../../presentation/visit.move.preview';
import { visitBookingOptions } from '../../presentation/visit.booking.options';
import { joinableVisitLabels, VisitMoveTargetOption, visitMoveTargetOptions } from '../../presentation/visit.move.target';
import { visitRescheduleRuleText } from '../../presentation/visit.reschedule.rule';
import { VisitMovePreviewComponent } from '../../section.renderers/visit.move.preview';
import { VISIT_MOVE_PREVIEW_QUERY, VISIT_MOVE_TARGETS_QUERY, VisitBookingChoice, VisitMovePreview, VisitMovePreviewRequest, VisitMoveTarget, VisitMoveTargets } from '../../visit.movement.types';
import { appointmentFromModel, KEEP_APPROVED_HINT, sameAppointment, targetAppointment, VisitAppointmentModel, VisitMovePreviewAsk, VisitMovePreviewState, VisitRescheduleChoice, VisitRescheduleDestination, VisitRescheduleOutcome, VisitRescheduleSubmit, visitDatesDescription, visitDatesDialogTitle, visitDatesSubmitLabel, visitMoveDayLabel, visitMovePreviewHint, visitMovePreviewKey, visitMoveTargetsDescription } from './visit.dates.dialog.model';

const DESTINATION_OPTIONS = [
  { value: VisitRescheduleDestination.Date, label: 'Pick a date' },
  { value: VisitRescheduleDestination.Visit, label: 'Join an upcoming visit' },
];

const BOOKING_OPTIONS = visitBookingOptions(false);


const PREVIEW_DEBOUNCE_MS = 250;

/** The preview section renderer's inputs. */
type VisitMovePreviewInputs = { summary: VisitMovePreviewSummary | null; loading: boolean; error: string | null; stale: boolean; hint: string };

/** The RadioGroup editor's inputs for the "Visit to join" field. */
type VisitMoveTargetInputs = { options: VisitMoveTargetOption[]; description: string };

const PREVIEW_FAILED = 'The preview could not be loaded. You can still reschedule; the server checks the move.';

@Component({
  selector: 'wb-visit-dates-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WbSurfaceComponent],
  template: '<wb-surface [definition]="$definition()" />',
})
export class VisitDatesDialogComponent {
  private readonly dialog = injectDialogRef<VisitRescheduleOutcome>();
  private readonly data = inject(EntityDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly previewRequests = new Subject<VisitMovePreviewAsk | null>();
  private readonly $state = signal<VisitMovePreviewState>({ key: null, loading: false, stale: false });
  /** The form as last emitted: the preview hint reads whether the person is joining. */
  private readonly $latestModel = signal<VisitAppointmentModel | null>(null);
  public readonly $row = input.required<VisitBrowseRow>({ alias: 'row' });
  /** Dispatch of a passed appointment: the person confirms or moves the date before a Job is built. */
  public readonly $decision = input(false, { alias: 'decision' });
  /** Offers "Keep approved" (on): the move is previewed and submitted as one approval of the moved appointment. */
  public readonly $approvable = input(false, { alias: 'approvable' });
  /** When given, the dialog runs the move and stays open on a stale review; otherwise it closes with the choice. */
  public readonly $submit = input<VisitRescheduleSubmit | undefined>(undefined, { alias: 'submit' });
  /** Whether the move keeps the approval as the form now stands; the targets are read for that answer. */
  private readonly $keepApproved = computed(() => this.keepsApproval(this.$latestModel()));
  /** Upcoming Visits the work may join; a passed appointment's decision keeps its own date, so it never asks. */
  private readonly targets = resource({
    params: () => this.$decision() ? undefined : { id: this.$row()._id, ...(this.$keepApproved() ? { approve: true } : {}) },
    loader: ({ params }) => this.data.executeCommand<VisitMoveTargets>(ActivityEntityName.Visit, VISIT_MOVE_TARGETS_QUERY, params),
  });
  private readonly $targetList = computed<VisitMoveTarget[]>(() => this.targets.hasValue() ? this.targets.value().targets : []);

  public constructor() {

    this.previewRequests
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        switchMap(ask => ask ? this.fetchPreview(ask) : of<VisitMovePreviewState>({ key: null, loading: false, stale: false })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(state => this.$state.set(state));
  }

  protected readonly $definition = computed(() => {
    const row = this.$row();
    const decision = this.$decision();
    const approvable = !decision && this.$approvable();
    const now = new Date();
    const floor = visitRescheduleFloor(row, now);
    const pastDateRules = visitRescheduleRuleText(now, row.timezone);
    const description = visitDatesDescription(row, decision, now, pastDateRules);
    const booked = (row.bookings ?? []).length > 0;
    const hasWindow = hasVisitComplianceWindow(row);
    const startDate = new Date(row.startDate);
    const endDate = new Date(row.endDate);
    const initialModel: VisitAppointmentModel = {
      destination: VisitRescheduleDestination.Date,
      date: DateTime.fromJSDate(startDate, { zone: row.timezone }).startOf('day').toJSDate(),
      startTime: startDate,
      endTime: endDate > startDate ? endDate : DateTime.fromJSDate(startDate, { zone: row.timezone }).plus({ days: 1 }).toJSDate(),
      allDay: row.allDay === true,
      ...(booked ? { booking: VisitBookingChoice.Keep } : {}),
      allowOutsideWindow: false,
      ...(approvable ? { keepApproved: true } : {}),
    };
    const schema = defineSurfaceSchema(ActivityEntityName.Visit, SurfaceType.Form)
      .model<VisitAppointmentModel>()
      .submitLabel(visitDatesSubmitLabel(decision))
      .body(body => {
        body.section('dates', section => {
          section
            .title(`${visitDatesDialogTitle(decision)} · ${row.timezone}`)
            .columns(1);
          if (!decision) section.field({ bind: 'destination', required: true, metadata: { type: MetadataEntityFieldType.Enum, editor: MetadataEntityFieldEditorType.ButtonSelect, displayName: 'Move to', options: DESTINATION_OPTIONS } });
          section
            .field({ bind: 'date', required: true, autofocus: true, metadata: { type: MetadataEntityFieldType.Date, displayName: 'Date' }, editorInputs: { timezone: row.timezone, min: floor } })
            .field({ bind: 'startTime', required: true, metadata: { type: MetadataEntityFieldType.Time, displayName: 'Start time' }, editorInputs: { timezone: row.timezone } })
            .field({ bind: 'endTime', required: true, metadata: { type: MetadataEntityFieldType.Time, displayName: 'End time' }, editorInputs: { timezone: row.timezone } })
            .field({ bind: 'allDay', metadata: { type: MetadataEntityFieldType.Boolean, displayName: 'All day', readonly: false } });
          if (booked) section.field({ bind: 'booking', required: true, metadata: { type: MetadataEntityFieldType.Enum, displayName: 'Booking', options: BOOKING_OPTIONS } });
          if (hasWindow) section.field({ bind: 'allowOutsideWindow', metadata: { type: MetadataEntityFieldType.Boolean, displayName: 'Allow a date outside the compliance window', readonly: false } });
          if (approvable) section.field({ bind: 'keepApproved', metadata: { type: MetadataEntityFieldType.Boolean, displayName: 'Keep approved', description: KEEP_APPROVED_HINT, readonly: false } });
          if (description) section.description(description);
        });
        // Each upcoming Visit is one radio row; a refused one is listed, disabled, with the server's reason.
        if (!decision) body.section('target', section => section.title('Upcoming visit').columns(1).field({ bind: 'targetVisitId', metadata: { type: MetadataEntityFieldType.Enum, editor: MetadataEntityFieldEditorType.RadioGroup, displayName: 'Visit to join' }, editorInputs: 'visit.moveTargets' }));
        body.section('preview', section => section.title('What happens').sectionRenderer('visit.movePreview'));
      })
      .rules(rules => {
        rules.rule('all-day-times', rule => rule.when(condition => condition.eq('allDay', true))
          .then(effect => effect.hide({ type: 'field', id: 'startTime' }), effect => effect.hide({ type: 'field', id: 'endTime' })));
        if (decision) return;
        // Joining adopts the target's appointment, and every listed target is inside the window.
        const joinHides = ['date', 'startTime', 'endTime', 'allDay', ...(hasWindow ? ['allowOutsideWindow'] : [])];
        rules.rule('join-visit', rule => rule.when(condition => condition.eq('destination', VisitRescheduleDestination.Visit))
          .then(...joinHides.map(id => effect => effect.hide({ type: 'field', id }))));
        rules.rule('pick-date', rule => rule.when(condition => condition.eq('destination', VisitRescheduleDestination.Date))
          .then(effect => effect.hide({ type: 'section', id: 'body.target' })));
      })
      .build();

    return defineSurface<VisitAppointmentModel>({
      schema,
      initialModel,
      mode: 'create',
      runtime: defineSurfaceRuntime<VisitAppointmentModel>({
        sectionRenderers: {
          'visit.movePreview': { component: VisitMovePreviewComponent, inputs: () => computed(() => this.previewInputs(row, decision)) },
        },
        editorInputs: {
          'visit.moveTargets': { inputs: () => computed(() => this.targetInputs(row)) },
        },
      }),
      onModelChange: event => {
        this.$latestModel.set(event.model);
        this.$state.update(state => ({ ...state, stale: false }));
        this.previewRequests.next(this.previewRequest(row, event.model, now));
      },
      onSubmit: async event => {
        const joining = event.model.destination === VisitRescheduleDestination.Visit;
        const target = joining ? this.selectedTarget(event.model) : undefined;
        if (joining && !target) throw new Error('Choose the visit to join.');
        const dates = target ? targetAppointment(target) : appointmentFromModel(row, event.model);
        if (!target && !visitRescheduleAllowed(row, DateTime.fromJSDate(dates.startDate, { zone: row.timezone }).toISODate()!, now)) throw new Error(pastDateRules);
        const ask = this.previewRequest(row, event.model, now);
        const preview = ask ? await this.currentPreview(ask) : undefined;
        if (preview) {
          const summary = summariseVisitMovePreview(preview, visitMoveDayLabel(row, dates));
          if (summary.blocked) throw new Error(preview.refusals[0]?.message ?? 'This visit cannot move to that date.');
        }
        const choice: VisitRescheduleChoice = {
          dates,
          ...(target ? { targetVisitId: target._id } : {}),
          ...(event.model.booking ? { booking: event.model.booking } : {}),
          // Joining hides the window toggle and previews without it, so a value left from a picked date must not reach the server.
          allowOutsideWindow: !target && event.model.allowOutsideWindow === true,
          ...(preview ? { fingerprint: preview.fingerprint } : {}),
          ...(this.keepsApproval(event.model) ? { keepApproved: true } : {}),
        };
        const submit = this.$submit();
        if (!submit) {
          this.dialog.close({ choice });
          return event.model;
        }
        const result = await submit(choice);
        // The run already reported why it failed; stay open so the person can retry or cancel.
        if (!result) return event.model;
        const refreshed = result.movement?.refreshedPreview;
        if (refreshed) {
          // Nothing moved: show what the move would do now and let the person confirm it again.
          this.$state.set({ key: ask ? visitMovePreviewKey(ask.request) : null, date: ask?.date, loading: false, preview: refreshed, stale: true });
          return event.model;
        }
        this.dialog.close({ choice, result });
        return event.model;
      },
      onCancel: () => this.dialog.dismiss(),
    });
  });

  /** The preview panel's inputs; the spacing warning is client-side, the server never refuses it. */
  private previewInputs(row: VisitBrowseRow, decision: boolean): VisitMovePreviewInputs {

    const state = this.$state();
    const joining = this.$latestModel()?.destination === VisitRescheduleDestination.Visit;
    const hint = visitMovePreviewHint(decision, joining);
    if (!state.preview) return { summary: null, loading: state.loading, error: state.error ?? null, stale: state.stale, hint };
    const date = state.date;
    // Spacing is a commitment's rhythm, so only fully approved work (or a line that needs none) is warned about it.
    const spacing = isFullyApproved(row.approvalStatus) && date ? visitSpacingWarning(row.spacing, date) : undefined;
    const label = date ? DateTime.fromISO(date).toFormat('d LLL') : 'that date';
    // Naming other visits only helps a picked date; a joined visit is already the person's choice.
    const joinable = joining ? [] : joinableVisitLabels(this.$targetList(), row.timezone);

    return { summary: summariseVisitMovePreview(state.preview, label, { spacing, joinable }), loading: state.loading, error: null, stale: state.stale, hint };
  }

  /** The "Visit to join" radio rows; while they load or fail the field's description says so. */
  private targetInputs(row: VisitBrowseRow): VisitMoveTargetInputs {

    const options = visitMoveTargetOptions(this.$targetList(), row.timezone);
    return { options, description: visitMoveTargetsDescription(this.targets.isLoading(), !!this.targets.error(), options.length) };
  }

  /** The picked target while it is still listed and allowed. */
  private selectedTarget(model: VisitAppointmentModel): VisitMoveTarget | undefined {

    return this.$targetList().find(target => target._id === model.targetVisitId && !target.refusals.length);
  }

  /** True when the dialog offers Keep approved and the person left it on. */
  private keepsApproval(model: VisitAppointmentModel | null): boolean {

    return this.$approvable() && !this.$decision() && model?.keepApproved !== false;
  }

  /** Null when there is nothing to preview: an invalid appointment, the unchanged one (a confirmation), or no target yet. */
  private previewRequest(row: VisitBrowseRow, model: VisitAppointmentModel, now: Date): VisitMovePreviewAsk | null {

    const approve = this.keepsApproval(model);
    if (model.destination === VisitRescheduleDestination.Visit) return this.targetPreviewRequest(row, model, approve);
    let dates: VisitDateOverride;
    try {
      dates = appointmentFromModel(row, model);
    } catch {
      return null;
    }
    const date = DateTime.fromJSDate(dates.startDate, { zone: row.timezone }).toISODate()!;
    if (!visitRescheduleAllowed(row, date, now)) return null;
    if (sameAppointment(row, dates)) return null;
    const request: VisitMovePreviewRequest = {
      id: row._id,
      dates,
      ...(model.booking ? { booking: model.booking } : {}),
      ...(model.allowOutsideWindow ? { allowOutsideWindow: true } : {}),
      ...(approve ? { approve: true } : {}),
    };

    return { request, date };
  }

  /** Joining previews by target, approving or not, so the fingerprint matches the join the server re-previews. */
  private targetPreviewRequest(row: VisitBrowseRow, model: VisitAppointmentModel, approve: boolean): VisitMovePreviewAsk | null {

    const target = this.selectedTarget(model);
    if (!target) return null;
    const request: VisitMovePreviewRequest = {
      id: row._id,
      targetVisitId: target._id,
      ...(model.booking ? { booking: model.booking } : {}),
      ...(approve ? { approve: true } : {}),
    };

    return { request, date: target.localDate };
  }

  /** The preview submit confirms: the shown one when it answers this request, else a fresh read. A failed read refuses the submit. */
  private async currentPreview(ask: VisitMovePreviewAsk): Promise<VisitMovePreview> {

    const state = this.$state();
    if (state.preview && state.key === visitMovePreviewKey(ask.request)) return state.preview;
    const read = await lastValueFrom(this.fetchPreview(ask));
    this.$state.set(read);
    if (!read.preview) throw new Error(read.error ?? PREVIEW_FAILED);

    return read.preview;
  }

  /** One preview read as panel state: loading first, then the preview or the server's reason it failed. */
  private fetchPreview(ask: VisitMovePreviewAsk): Observable<VisitMovePreviewState> {

    const key = visitMovePreviewKey(ask.request);
    this.$state.set({ key, date: ask.date, loading: true, stale: false });

    return from(this.data.executeCommand<VisitMovePreview>(ActivityEntityName.Visit, VISIT_MOVE_PREVIEW_QUERY, ask.request))
      .pipe(
        map((preview): VisitMovePreviewState => ({ key, date: ask.date, loading: false, preview, stale: false })),
        catchError((error: unknown) => of<VisitMovePreviewState>({ key, date: ask.date, loading: false, error: resolveBulkActionErrorMessage(error, PREVIEW_FAILED), stale: false })),
      );
  }
}
